import { useCallback } from 'react';

import { trackEvent } from '@zep/logger/tracking';

import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { createUserAttachment } from '../stores/aiBuilderAttachments';
import {
  createAiBuilderUserMessageId,
  updateAiBuilderUserMessageStatus,
} from '../stores/aiBuilderMessageState';
import { getActiveChoiceMessage } from '../stores/aiBuilderMessageStream';
import {
  createFailedSourcePreparationProgressMessage,
  createSourcePreparationProgressId,
  createSourcePreparationProgressMessage,
} from '../stores/aiBuilderSourcePreparationMessage';
import {
  createAiBuilderClientRequestId,
  createMessageTurnRequest,
} from '../stores/aiBuilderTurnRequests';
import { isAiBuilderUsageBlocked } from '../stores/aiBuilderUsagePolicy';
import {
  type AiBuilderChatActions,
  type AiBuilderChatState,
  type AiBuilderSelectedQuizContext,
  type AiBuilderSubmitPromptOptions,
  type AiBuilderUserMessageRetryRequest,
} from '../stores/types';
import { type useDispatchServerTurn } from '../stores/useAiBuilderServerTurnDispatcher';
import { isAiBuilderAuthExpiredError } from '../utils/aiBuilderAuthExpiry';
import { aiBuilderChatAdapter } from '../utils/aiBuilderChatAdapter';
import { type AiBuilderChatCopy } from '../utils/aiBuilderChatCopy';
import { trackAiBuilderUsageBlocked } from '../utils/aiBuilderChatTracking';
import { createServerSourceRegistrationRequest } from '../utils/aiBuilderServerSources';
import { getServerAiBuilderChatErrorMessageFromError } from '../utils/serverAiBuilderChatErrorMessages';
import { type AiBuilderTurnRequest } from '../utils/types';

import { type useActiveSourceRefs } from './useActiveSourceRefs';

type UpdateAiBuilderChatState = (
  updater: (prev: AiBuilderChatState) => AiBuilderChatState,
) => void;
type AiBuilderMutableRef<T> = { current: T };
type GetNextAiBuilderRequestId = () => number;
type IsAiBuilderTurnBusy = () => boolean;

type CreateTurnContextParams = {
  signal?: AbortSignal;
  selectedQuizContext?: AiBuilderSelectedQuizContext | null;
  sourceRefs?: string[];
  turnHints?: AiBuilderSubmitPromptOptions['turnHints'];
};

type UseAiBuilderSubmitPromptParams = {
  activeRunIdRef: AiBuilderMutableRef<number>;
  activeSourceRefs: ReturnType<typeof useActiveSourceRefs>;
  appendUserMessage: (params: {
    attachment?: ReturnType<typeof createUserAttachment>;
    consumeChoiceMessageId?: string;
    content: string;
    requestId: number;
    retry?: AiBuilderUserMessageRetryRequest;
  }) => void;
  copy: AiBuilderChatCopy;
  createTurnContext: (
    params?: CreateTurnContextParams,
  ) => AiBuilderTurnRequest['context'];
  draftInstanceId: string;
  dispatchServerTurn: ReturnType<typeof useDispatchServerTurn>;
  ensureServerSessionForSourceRegistration: (
    runId: number,
  ) => Promise<string | null>;
  getNextRequestId: GetNextAiBuilderRequestId;
  isTurnBusy: IsAiBuilderTurnBusy;
  lastQuizListSnapshotRef: AiBuilderMutableRef<BuildQuizListItem[] | null>;
  getChatState: () => AiBuilderChatState;
  getLatestMessages: () => AiBuilderChatState['messages'];
  /** 로그인 만료 단일 처리기 — 스냅샷 저장 + 재로그인 모달. */
  onAuthExpired: () => void;
  selectedQuizContext: AiBuilderSelectedQuizContext | null;
  updateState: UpdateAiBuilderChatState;
};

export const useAiBuilderSubmitPrompt = ({
  activeRunIdRef,
  activeSourceRefs,
  appendUserMessage,
  copy,
  createTurnContext,
  draftInstanceId,
  dispatchServerTurn,
  ensureServerSessionForSourceRegistration,
  getNextRequestId,
  isTurnBusy,
  lastQuizListSnapshotRef,
  getChatState,
  getLatestMessages,
  onAuthExpired,
  selectedQuizContext,
  updateState,
}: UseAiBuilderSubmitPromptParams) => {
  return useCallback<AiBuilderChatActions['submitPrompt']>(
    (prompt, options) => {
      const trimmedPrompt = prompt.trim();
      const { selectedFile, usage } = getChatState();
      const blocked = !trimmedPrompt || isTurnBusy();

      if (blocked) {
        return;
      }

      if (isAiBuilderUsageBlocked(usage)) {
        trackAiBuilderUsageBlocked(usage, 'prompt');
        return;
      }

      if (selectedFile) {
        const selectedFileBlob = selectedFile.file;
        const pageCount =
          'pageCount' in selectedFile ? selectedFile.pageCount : undefined;
        const pageRanges = selectedFile.pageRanges?.map(range => ({
          ...range,
        }));
        const requestId = getNextRequestId();
        const clientRequestId = createAiBuilderClientRequestId();
        const activeChoiceMessage = getActiveChoiceMessage(getLatestMessages());
        const userMessageId = createAiBuilderUserMessageId(requestId);
        const sourcePreparationRunId = activeRunIdRef.current + 1;
        const sourcePreparationProgressId = createSourcePreparationProgressId(
          sourcePreparationRunId,
        );
        activeRunIdRef.current = sourcePreparationRunId;
        appendUserMessage({
          consumeChoiceMessageId: activeChoiceMessage?.id,
          content: trimmedPrompt,
          requestId,
        });
        lastQuizListSnapshotRef.current = null;
        updateState(prev => ({
          ...prev,
          turnInProgress: true,
          messages: [
            ...prev.messages,
            createSourcePreparationProgressMessage(
              sourcePreparationRunId,
              copy,
            ),
          ],
        }));

        void (async () => {
          try {
            const sessionId = await ensureServerSessionForSourceRegistration(
              sourcePreparationRunId,
            );

            if (
              activeRunIdRef.current !== sourcePreparationRunId ||
              !sessionId
            ) {
              return;
            }

            const sourceResult = await aiBuilderChatAdapter.registerSource(
              createServerSourceRegistrationRequest({
                draftInstanceId,
                request: {
                  sourceType: 'file',
                  file: selectedFileBlob,
                  fileName: selectedFileBlob.name,
                  ...(pageRanges ? { pageRanges } : {}),
                },
                sessionId,
              }),
            );

            if (activeRunIdRef.current !== sourcePreparationRunId) return;

            if (sourceResult.status !== 'ready') {
              throw new Error('Server source registration failed.');
            }

            activeSourceRefs.recordSourceType(sourceResult.sourceRef, 'file');
            trackEvent('quiz_ai_builder_source_attach', {
              source_type: 'file',
              status: 'ready',
              session_id: sessionId,
              ...(pageRanges
                ? {
                    selected_page_count: pageRanges.reduce(
                      (count, range) => count + range.end - range.start + 1,
                      0,
                    ),
                    range_count: pageRanges.length,
                  }
                : {}),
            });

            const attachment = createUserAttachment(
              trimmedPrompt,
              pageRanges && pageCount != null && pageCount !== 0
                ? {
                    file: selectedFileBlob,
                    pageCount,
                    pageRanges,
                  }
                : { file: selectedFileBlob },
            );
            const sourceRefs = [
              ...activeSourceRefs.consume(),
              sourceResult.sourceRef,
            ];
            const retryRequest: AiBuilderUserMessageRetryRequest = {
              clientRequestId,
              type: 'message',
              prompt: trimmedPrompt,
              selectedQuizContext,
              sourceRefs,
              ...(attachment ? { attachment } : {}),
              ...(options?.turnHints ? { turnHints: options.turnHints } : {}),
            };
            updateState(prev => ({
              ...prev,
              sourceRefs: prev.sourceRefs.includes(sourceResult.sourceRef)
                ? prev.sourceRefs
                : [...prev.sourceRefs, sourceResult.sourceRef],
              messages: prev.messages
                .filter(message => message.id !== sourcePreparationProgressId)
                .map(message =>
                  message.kind === 'user' && message.id === userMessageId
                    ? {
                        ...message,
                        ...(attachment ? { attachment } : {}),
                        retry: retryRequest,
                      }
                    : message,
                ),
            }));

            dispatchServerTurn(
              aiBuilderChatAdapter.startTurn,
              signal =>
                createMessageTurnRequest({
                  requestId,
                  clientRequestId,
                  clientCopy: copy,
                  message: trimmedPrompt,
                  context: createTurnContext({
                    signal,
                    selectedQuizContext,
                    sourceRefs,
                    ...(options?.turnHints
                      ? { turnHints: options.turnHints }
                      : {}),
                  }),
                }),
              { userMessageId },
            );
          } catch (error) {
            trackEvent('quiz_ai_builder_source_attach', {
              source_type: 'file',
              status: 'error',
              session_id: getChatState().sessionId ?? '',
            });

            if (activeRunIdRef.current !== sourcePreparationRunId) return;

            // 첨부 등록(세션 생성 + registerSource)은 턴 스트림 밖에서 직접 await 하므로
            // 스트림 catch 의 만료 분기가 닿지 않는다. 여기서 걸러내지 않으면 만료가
            // 일반 "자료 등록 실패" 로 뭉개져 재로그인 안내가 통째로 사라진다.
            const authExpired = isAiBuilderAuthExpiredError(error);

            updateState(prev => {
              const settledMessages = updateAiBuilderUserMessageStatus({
                messages: prev.messages.map(message =>
                  message.id === sourcePreparationProgressId
                    ? createFailedSourcePreparationProgressMessage(
                        sourcePreparationRunId,
                        copy,
                      )
                    : message,
                ),
                messageId: userMessageId,
                status: 'failed',
              });

              return {
                ...prev,
                isGenerating: false,
                turnInProgress: false,
                selectedFile,
                // 만료는 재로그인 모달이 안내를 담당한다(SSE 경로와 같은 규칙).
                messages: authExpired
                  ? settledMessages
                  : [
                      ...settledMessages,
                      {
                        id: `system-error-${Date.now()}`,
                        kind: 'system',
                        tone: 'error',
                        content:
                          getServerAiBuilderChatErrorMessageFromError(
                            error,
                            copy,
                          ) ?? copy.system.sourceRegistrationFailed,
                      },
                    ],
              };
            });

            // 스냅샷이 정리된 메시지를 담도록 상태 반영 뒤에 호출한다.
            if (authExpired) {
              onAuthExpired();
            }
          }
        })();

        return;
      }

      const requestId = getNextRequestId();
      const attachment = createUserAttachment(trimmedPrompt, selectedFile);
      const clientRequestId = createAiBuilderClientRequestId();
      const sourceRefs = activeSourceRefs.consume();
      const activeChoiceMessage = getActiveChoiceMessage(getLatestMessages());
      const userMessageId = createAiBuilderUserMessageId(requestId);
      const retryRequest: AiBuilderUserMessageRetryRequest = {
        clientRequestId,
        type: 'message',
        prompt: trimmedPrompt,
        selectedQuizContext,
        sourceRefs,
        ...(attachment ? { attachment } : {}),
        ...(options?.turnHints ? { turnHints: options.turnHints } : {}),
      };

      appendUserMessage({
        attachment,
        consumeChoiceMessageId: activeChoiceMessage?.id,
        content: trimmedPrompt,
        requestId,
        retry: retryRequest,
      });
      lastQuizListSnapshotRef.current = null;

      dispatchServerTurn(
        aiBuilderChatAdapter.startTurn,
        signal =>
          createMessageTurnRequest({
            requestId,
            clientRequestId,
            clientCopy: copy,
            message: trimmedPrompt,
            context: createTurnContext({
              signal,
              selectedQuizContext,
              sourceRefs,
              ...(options?.turnHints ? { turnHints: options.turnHints } : {}),
            }),
          }),
        { userMessageId },
      );
    },
    [
      activeRunIdRef,
      activeSourceRefs,
      appendUserMessage,
      copy,
      createTurnContext,
      draftInstanceId,
      dispatchServerTurn,
      ensureServerSessionForSourceRegistration,
      getNextRequestId,
      getChatState,
      getLatestMessages,
      isTurnBusy,
      lastQuizListSnapshotRef,
      onAuthExpired,
      selectedQuizContext,
      updateState,
    ],
  );
};
